import React, { useState, useEffect } from 'react';
import { cn } from '../utils/cn';
import Icon from './AppIcon';
import lumoStrategyService from '../services/lumoStrategyService';

/**
 * Real-Time Coaching Input
 * Shows live feedback as user types with avoid phrase detection
 */

export const RealTimeCoachingInput = ({
  value,
  onChange,
  onSubmit,
  placeholder,
  personaType = 'CFO',
  disabled = false,
  minLength = 0,
  maxLength = 500,
  className
}) => {
  const [avoidPhrases, setAvoidPhrases] = useState([]);
  const [detectedPhrases, setDetectedPhrases] = useState([]);
  const [stats, setStats] = useState({
    charCount: 0,
    wordCount: 0,
    questionCount: 0,
    statementCount: 0
  });

  useEffect(() => {
    // Load avoid phrases for persona
    const phrases = lumoStrategyService.getAvoidPhrases(personaType);
    setAvoidPhrases(phrases);
  }, [personaType]);

  useEffect(() => {
    // Analyze input as user types
    if (!value) {
      setDetectedPhrases([]);
      setStats({
        charCount: 0,
        wordCount: 0,
        questionCount: 0,
        statementCount: 0
      });
      return;
    }

    // Detect salesy phrases
    const detected = avoidPhrases.filter(phrase =>
      value.toLowerCase().includes(phrase.toLowerCase())
    );
    setDetectedPhrases(detected);

    // Calculate stats
    const charCount = value.length;
    const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
    const questionCount = (value.match(/\?/g) || []).length;
    const statementCount = (value.match(/\./g) || []).length;

    setStats({
      charCount,
      wordCount,
      questionCount,
      statementCount
    });
  }, [value, avoidPhrases]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  // Calculate coaching signals
  const isToLong = stats.charCount > 100;
  const isTooManyStatements = stats.statementCount > 3 && stats.questionCount === 0;
  const hasGoodProbe = stats.questionCount > 0 && stats.charCount < 60;
  const needsPhraseAlert = detectedPhrases.length > 0;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Text Area */}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className={cn(
          'w-full px-4 py-3 rounded-lg border bg-background',
          'focus:ring-2 focus:ring-primary focus:border-transparent',
          'disabled:bg-gray-100 disabled:cursor-not-allowed',
          'resize-none',
          needsPhraseAlert && 'border-red-300',
          hasGoodProbe && 'border-green-300',
          className
        )}
        rows={4}
      />

      {/* Real-Time Coaching Feedback */}
      <div className="space-y-2">
        {/* Avoid Phrases Alert */}
        {needsPhraseAlert && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <Icon name="AlertTriangle" size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="text-red-900 font-semibold mb-1">
                Salesy Language Detected
              </p>
              <div className="space-y-1">
                {detectedPhrases.map((phrase, idx) => (
                  <p key={idx} className="text-red-700">
                    • Avoid: "<span className="font-mono">{phrase}</span>"
                  </p>
                ))}
              </div>
              <p className="text-red-600 text-xs mt-2">
                💡 Try neutral acknowledgment or a frame-flipping question instead
              </p>
            </div>
          </div>
        )}

        {/* Too Long Warning */}
        {isToLong && !needsPhraseAlert && (
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Icon name="MessageSquare" size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="text-yellow-900 font-semibold">Response Getting Long</p>
              <p className="text-yellow-700">
                Trim to a single sharp question for maximum impact (under 60 chars ideal)
              </p>
            </div>
          </div>
        )}

        {/* Too Many Statements Warning */}
        {isTooManyStatements && !needsPhraseAlert && !isToLong && (
          <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <Icon name="MessageCircle" size={16} className="text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="text-orange-900 font-semibold">Too Much Telling</p>
              <p className="text-orange-700">
                Replace statements with a question — ask, don't explain
              </p>
            </div>
          </div>
        )}

        {/* Good Probe Celebration */}
        {hasGoodProbe && !needsPhraseAlert && (
          <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="text-green-900 font-semibold">Sharp Micro-Probe</p>
              <p className="text-green-700">
                Concise question — that's the Lumo move
              </p>
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <div className="flex items-center justify-between text-xs text-gray-600 px-2">
          <div className="flex items-center gap-4">
            <span className={cn(
              stats.charCount < 60 ? 'text-green-600' : stats.charCount > 100 ? 'text-red-600' : 'text-gray-600'
            )}>
              {stats.charCount} chars
            </span>
            <span className={cn(
              stats.questionCount > 0 ? 'text-green-600' : 'text-gray-600'
            )}>
              {stats.questionCount} ?
            </span>
            <span className={cn(
              stats.statementCount > 3 ? 'text-orange-600' : 'text-gray-600'
            )}>
              {stats.statementCount} .
            </span>
          </div>
          <span className="text-gray-500">
            {minLength > 0 && stats.charCount < minLength && (
              <span className="text-orange-600">
                {minLength - stats.charCount} more needed
              </span>
            )}
            {maxLength > 0 && (
              <span className={stats.charCount > maxLength * 0.9 ? 'text-red-600' : ''}>
                {maxLength - stats.charCount} left
              </span>
            )}
          </span>
        </div>
      </div>

      <p className="text-xs text-gray-500 px-2">
        💡 Press Ctrl+Enter to send • Keep it sharp and avoid salesy jargon
      </p>
    </div>
  );
};

export default RealTimeCoachingInput;
